import { useTranslation } from 'react-i18next'
import {
  CalendarIcon,
  ErrorCircleIcon,
  InfoCircleIcon,
  Span,
  Text,
} from '@/components/atoms'
import { CalendarPanel } from '../calendar-panel'
import { formatDate } from '@/shared/helpers/format-date'
import { useDatePickerField } from './date-picker-field.hook'

interface DatePickerFieldProps {
  id?: string
  label?: string
  value: Date | undefined
  placeholder?: string
  helpText?: string
  error?: boolean | string
  tooltip?: string
  optional?: boolean
  disabled?: boolean
  withActions?: boolean
  dropdownMonthYear?: boolean
  panelPosition?: 'absolute' | 'relative'
  dateFormatOptions?: Intl.DateTimeFormatOptions
  minDate?: Date
  maxDate?: Date
  onChange: (date: Date) => void
  onBlur?: () => void
  'data-testid'?: string
}

const DEFAULT_DATE_FORMAT_OPTIONS: Intl.DateTimeFormatOptions = {
  month: '2-digit',
  day: '2-digit',
  year: 'numeric',
}

export function DatePickerField({
  id,
  label,
  value,
  placeholder,
  helpText,
  error,
  tooltip,
  optional,
  disabled = false,
  withActions = false,
  dropdownMonthYear = false,
  panelPosition = 'absolute',
  dateFormatOptions = DEFAULT_DATE_FORMAT_OPTIONS,
  minDate,
  maxDate,
  onChange,
  onBlur,
  'data-testid': testId,
}: DatePickerFieldProps) {
  const { t } = useTranslation()
  const {
    ref,
    isOpen,
    stagedDate,
    displayedDate,
    hasError,
    displayedHelp,
    resolvedPlaceholder,
    triggerClasses,
    iconClassname,
    viewDate,
    prevMonth,
    nextMonth,
    goToDate,
    days,
    toggle,
    handleDayClick,
    handleConfirm,
    close,
  } = useDatePickerField({
    value,
    disabled,
    withActions,
    error,
    helpText,
    placeholder,
    onChange,
    onBlur,
  })

  return (
    <div className="flex w-full flex-col gap-3">
      {label && (
        <div className="flex items-center gap-1">
          <Text as="label" htmlFor={id} className="text-neutral-900">
            {label}
            {optional && (
              <Text as="span" className="text-neutral-300">
                {` (${t('common.optional')})`}
              </Text>
            )}
          </Text>
          {tooltip && (
            <div className="tooltip" data-tip={tooltip}>
              <InfoCircleIcon className="text-indigo-base h-4 w-4" />
            </div>
          )}
        </div>
      )}

      <div ref={ref} className="relative">
        <button
          id={id}
          type="button"
          data-testid={testId}
          disabled={disabled}
          className={triggerClasses}
          onClick={toggle}
        >
          {displayedDate
            ? formatDate(displayedDate, dateFormatOptions)
            : resolvedPlaceholder}
          <div className="absolute top-6 right-0 -translate-y-1/2 p-3">
            {hasError ? (
              <ErrorCircleIcon className={iconClassname} />
            ) : (
              <CalendarIcon className={iconClassname} />
            )}
          </div>
        </button>
        <div
          className={[
            panelPosition === 'absolute'
              ? 'absolute right-0 left-0 z-(--z-dropdown)'
              : 'relative',
            'mt-1 grid transition-[grid-template-rows,opacity] duration-300 ease-in-out',
            isOpen
              ? 'grid-rows-[1fr] opacity-100'
              : 'pointer-events-none grid-rows-[0fr] opacity-0',
          ].join(' ')}
        >
          <div
            className={`overflow-hidden ${panelPosition === 'absolute' ? 'rounded-2xl shadow-md' : ''}`}
          >
            <CalendarPanel
              viewDate={viewDate}
              days={days}
              onPrevMonth={prevMonth}
              onNextMonth={nextMonth}
              selectedDate={withActions ? stagedDate : value}
              minDate={minDate}
              maxDate={maxDate}
              onDayClick={handleDayClick}
              onConfirm={withActions ? handleConfirm : undefined}
              onCancel={withActions ? close : undefined}
              dropdownMonthYear={dropdownMonthYear}
              onSetViewDate={dropdownMonthYear ? goToDate : undefined}
            />
          </div>
        </div>
      </div>

      {displayedHelp && (
        <Span className={hasError ? 'text-error-base' : 'text-neutral-900'}>
          {displayedHelp}
        </Span>
      )}
    </div>
  )
}
